import type { FeatureKey, Features } from "@baton/api";
import { modeRouteKey, validateRedirect, type Runtime } from "@baton/core";
import { messageKey, type MessageKey } from "@baton/i18n";
import { Briefcase, Handshake, House, Settings, Ticket } from "@baton/icons";
import { isFeatureAvailable } from "@baton/ui";
import { holdsArea, seesArea, type AccessArea, type AccessGrant, type AccessLevel } from "../state/access.ts";
import type { ComponentType, ReactNode } from "react";
import { CLIENTS_DESTINATIONS, ClientsSidebar } from "../sidebar/ClientsSidebar.tsx";
import { CLIENTS_PREFIXES } from "./clientScope.ts";
import { HOME_DESTINATIONS, HomeSidebar } from "../sidebar/HomeSidebar.tsx";
import { SETTINGS_DESTINATIONS, SettingsSidebar } from "../sidebar/SettingsSidebar.tsx";
import { TICKETS_DESTINATIONS, TicketsSidebar } from "../sidebar/TicketsSidebar.tsx";
import { WORK_DESTINATIONS, WorkSidebar } from "../sidebar/WorkSidebar.tsx";

/**
 * FR-015-002, FR-015-005, FR-015-071, `contracts/screens.md` § The rail and the work surface
 * and § Sidebar: the rail is a column of modes, after ClickUp's global navigation. A mode owns
 * the first path segments below `/{ws}/`, a landing route, the destinations its sidebar lists
 * (which the palette's navigate mode reads as well) and its sidebar component, so no
 * destination sits in both the rail and the sidebar. The mode on screen is derived from the
 * URL, never from UI state, so a deep link or a reload shows the right sidebar, and each mode
 * remembers its last route per workspace. A mode whose feature key the workspace read does not
 * carry has no tile. Only modes whose screens are built are registered here (Home, Work since
 * 2026-10-19 by the owner's decision, Clients with 020's dashboard slice, 2026-09-19 by the owner's
 * decision, Tickets with 028's slice 2, 2026-10-21, and Settings with slice 8); the later modes
 * and their prefixes are the table in `contracts/screens.md` § The rail and the work surface,
 * and each joins this list as one entry with its sidebar component.
 */
export interface ModeSidebarProps {
  readonly ws: string;
  readonly unread: number;
  readonly onNavigate?: () => void;
}

export interface ModeDestination {
  readonly id: string;
  /** The screen's own name, which the palette matches on. */
  readonly label: MessageKey;
  /** The route below `/{ws}/`. */
  readonly path: string;
  /** A page only a workspace owner or admin can read, absent from the sidebar and the palette for everyone else. */
  readonly adminOnly?: boolean;
  /** A page any team role but guest reads (018's meetings), absent from the sidebar and the palette for a guest. */
  readonly memberOnly?: boolean;
  /** A page inside an access area (020's client screens): absent from the sidebar and the palette for a caller with no grant for that area, rather than shown and refused. */
  readonly area?: AccessArea;
  /** The level of `area` the page needs, `read` when absent: a page of writes only, such as the milestone templates settings, needs `write`. */
  readonly areaLevel?: AccessLevel;
  /** The instance's own administration: shown to a team owner or admin who is also an instance admin, and to nobody else. */
  readonly instanceAdminOnly?: boolean;
}

export interface ModeSpec {
  readonly id: string;
  readonly feature: FeatureKey | null;
  /** A mode that belongs to an access area (020's clients): it has no rail tile at all for a caller with no grant for that area. Absent, not disabled. */
  readonly area?: AccessArea;
  /** The level of `area` the mode needs, any level when absent: Tickets needs `portal` (028 FR-028-045). */
  readonly areaLevel?: AccessLevel;
  readonly label: MessageKey;
  readonly icon: ReactNode;
  /** The first path segments below `/{ws}/` this mode owns. */
  readonly prefixes: readonly string[];
  /** The route below `/{ws}/` a tile opens when the mode has no remembered route. */
  readonly landing: string;
  readonly destinations: readonly ModeDestination[];
  readonly Sidebar: ComponentType<ModeSidebarProps>;
}

export const HOME_MODE: ModeSpec = {
  id: "home",
  feature: null,
  label: messageKey("nav.mode.home"),
  icon: <House aria-hidden="true" />,
  prefixes: ["dashboard", "overview", "inbox", "my-work", "search", "activity", "meetings"],
  landing: "dashboard",
  destinations: HOME_DESTINATIONS,
  Sidebar: HomeSidebar,
};

/** Work (2026-10-19, by the owner's decision after feedback that the product read as a software-development tool, its sprints in Home while Clients had a mode of its own): the Team's tasks, its backlog, sprints and projects, with the saved views of tasks, which Home owned until that day. Shown as Work; the access area it stands for is `dev` (020 § Access grants), which gates no key yet, so the mode carries no `area` and every member who read these pages in Home reads them here: tying it to the area would take them from members holding no `dev` grant, which the owner has not asked for. It joins the area when the area gates its keys. */
export const WORK_MODE: ModeSpec = {
  id: "work",
  feature: null,
  label: messageKey("nav.mode.work"),
  icon: <Briefcase aria-hidden="true" />,
  prefixes: ["all-tasks", "backlog", "archive", "sprints", "projects", "tasks", "views", "all-items", "items", "cycles"],
  landing: "all-tasks",
  destinations: WORK_DESTINATIONS,
  Sidebar: WorkSidebar,
};

/** Clients (020, 2026-09-19 by the owner's decision): the client list, the client groups, each client's dashboard, 022's Unassigned emails (2026-09-28) and 022's Library (2026-09-29), with the clients themselves in the mode's sidebar. The `reports` prefix left the mode with the Reports page, by the owner's decision of 2026-09-27, and the `deals` prefix with the deal records, by the owner's decision of 2026-09-28. The address bar says `clients` and `client-groups` since 2026-09-29, by the owner's review; the old `accounts` and `account-groups` stay prefixes of the mode for the moment their redirect takes. The mode belongs to the `clients` access area (020 § Access grants, 2026-09-21), so a caller with no grant for it has no tile, no sidebar rows and no palette entries: the area is absent rather than refused. */
export const CLIENTS_MODE: ModeSpec = {
  id: "clients",
  feature: null,
  area: "clients",
  label: messageKey("nav.mode.clients"),
  icon: <Handshake aria-hidden="true" />,
  prefixes: CLIENTS_PREFIXES,
  landing: "clients",
  destinations: CLIENTS_DESTINATIONS,
  Sidebar: ClientsSidebar,
};

/** Tickets (028 FR-028-045, 2026-10-21): every client's portal for a portal worker, a member of the Team who holds the `clients` area at `portal`, with the portals by client name in the mode's sidebar; a member below that level has no tile. A client member never reaches this shell: the portal shell draws their one tile (`portal/shell/PortalShell.tsx`). */
export const TICKETS_MODE: ModeSpec = {
  id: "tickets",
  feature: null,
  area: "clients",
  areaLevel: "portal",
  label: messageKey("nav.mode.tickets"),
  icon: <Ticket aria-hidden="true" />,
  prefixes: ["tickets"],
  landing: "tickets",
  destinations: TICKETS_DESTINATIONS,
  Sidebar: TicketsSidebar,
};

/** Settings (015 slice 8): the workspace settings pages, listed in the mode's sidebar rather than a second rail on the work surface. */
export const SETTINGS_MODE: ModeSpec = {
  id: "settings",
  feature: null,
  label: messageKey("nav.mode.settings"),
  icon: <Settings aria-hidden="true" />,
  prefixes: ["settings"],
  landing: "settings/general",
  destinations: SETTINGS_DESTINATIONS,
  Sidebar: SettingsSidebar,
};

export const MODES: readonly ModeSpec[] = [HOME_MODE, WORK_MODE, CLIENTS_MODE, TICKETS_MODE, SETTINGS_MODE];

/** The modes a workspace shows this reader, in rail order: a mode gated by a feature key is present only when the workspace read carries it, and a mode belonging to an access area only for a reader whose membership holds a grant for that area (020 § Access grants), at the mode's level where it names one (028's Tickets, at `portal`). */
export function availableModes(features: Features, reader: ModeReader, registry: readonly ModeSpec[] = MODES): readonly ModeSpec[] {
  return registry.filter((mode) => (mode.feature === null || isFeatureAvailable(features, mode.feature)) && (mode.area === undefined || (mode.areaLevel === undefined ? seesArea(reader.grants, mode.area) : holdsArea(reader.grants, mode.area, mode.areaLevel))));
}

/** What decides which modes a caller is shown. `workspaceMember` stays for the rows that turn on a team role rather than on an area. */
export interface ModeReader {
  readonly workspaceMember: boolean;
  readonly grants: readonly AccessGrant[];
}

/** The mode that owns a path, or null for a path no mode owns (the not-found screen). */
export function modeOfPath(modes: readonly ModeSpec[], ws: string, pathname: string): ModeSpec | null {
  const base = `/${ws}/`;
  if (!pathname.startsWith(base)) return null;
  const segment = pathname.slice(base.length).split("/")[0] ?? "";
  return modes.find((mode) => mode.prefixes.includes(segment)) ?? null;
}

/**
 * Whether a page lies under one of its mode's destinations rather than being one (015 `contracts/screens.md`
 * § Top bar, the block of 2026-10-07): a client, a client's tasks, meeting or task, a meeting, a sprint, a
 * project's pages, a task, a settings record. These are the pages whose top bar carries Back; a
 * destination the sidebar lists, and the mode's landing, is where Back would lead and carries none.
 */
export function isNestedPage(modes: readonly ModeSpec[], ws: string, pathname: string): boolean {
  const mode = modeOfPath(modes, ws, pathname);
  if (mode === null) return false;
  const rest = pathname.slice(`/${ws}/`.length).replace(/\/$/, "");
  return rest !== mode.landing && !mode.destinations.some((destination) => (destination.path.split("?")[0] ?? "") === rest);
}

/** Where a mode tile goes: the mode's remembered route in this workspace while that route still belongs to the mode, else its landing route. */
export function modeRouteFor(runtime: Runtime, ws: string, mode: ModeSpec): string {
  const stored = validateRedirect(runtime.storage.get(modeRouteKey, ws)?.[mode.id]);
  if (stored !== null && modeOfPath([mode], ws, stored.split(/[?#]/)[0] ?? "") === mode) return stored;
  return `/${ws}/${mode.landing}`;
}

export function rememberModeRoute(runtime: Runtime, ws: string, mode: ModeSpec, route: string): void {
  const current = runtime.storage.get(modeRouteKey, ws) ?? {};
  if (current[mode.id] === route) return;
  runtime.storage.set(modeRouteKey, { ...current, [mode.id]: route }, ws);
}
